import { useEffect, useState, useCallback } from 'react'

const ZOOM_STORAGE_KEY = 'wpa-zoom-level'
const ZOOM_LEVELS = [50, 60, 75, 90, 100, 110, 125, 150] as const
const DEFAULT_ZOOM = 100
const BASE_FONT_SIZE_PX = 16
const BASE_LINE_HEIGHT = 1.5
const BASE_LETTER_SPACING_EM = 0.02

function getStoredZoom(): number {
	try {
		const stored = localStorage.getItem(ZOOM_STORAGE_KEY)
		if (stored) {
			const value = parseInt(stored, 10)
			if (ZOOM_LEVELS.includes(value as (typeof ZOOM_LEVELS)[number])) {
				return value
			}
		}
	} catch {
		// ignore
	}
	return DEFAULT_ZOOM
}

function applyZoom(level: number) {
	const scale = level / 100
	const fontSizePx = BASE_FONT_SIZE_PX * scale
	const lineHeight = BASE_LINE_HEIGHT * scale
	const letterSpacingEm = BASE_LETTER_SPACING_EM * scale

	document.documentElement.style.fontSize = `${fontSizePx}px`
	document.documentElement.style.lineHeight = String(lineHeight)
	document.documentElement.style.letterSpacing = `${letterSpacingEm}em`
}

export function useZoom() {
	const [zoomLevel, setZoomLevelState] = useState<number>(() => getStoredZoom())

	useEffect(() => {
		applyZoom(zoomLevel)
	}, [zoomLevel])

	const setZoomLevel = useCallback((level: number) => {
		const minZoom = ZOOM_LEVELS[0]
		const maxZoom = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
		const clamped = Math.min(maxZoom, Math.max(minZoom, level))
		const nearest = ZOOM_LEVELS.reduce((prev, curr) =>
			Math.abs(curr - clamped) < Math.abs(prev - clamped) ? curr : prev,
		)
		setZoomLevelState(nearest)
		localStorage.setItem(ZOOM_STORAGE_KEY, String(nearest))
	}, [])

	const zoomIn = useCallback(() => {
		const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel as (typeof ZOOM_LEVELS)[number])
		if (currentIndex < ZOOM_LEVELS.length - 1) {
			setZoomLevel(ZOOM_LEVELS[currentIndex + 1])
		}
	}, [zoomLevel, setZoomLevel])

	const zoomOut = useCallback(() => {
		const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel as (typeof ZOOM_LEVELS)[number])
		if (currentIndex > 0) {
			setZoomLevel(ZOOM_LEVELS[currentIndex - 1])
		}
	}, [zoomLevel, setZoomLevel])

	const resetZoom = useCallback(() => {
		setZoomLevel(DEFAULT_ZOOM)
	}, [setZoomLevel])

	return { zoomLevel, setZoomLevel, zoomIn, zoomOut, resetZoom, zoomLevels: ZOOM_LEVELS }
}
